/**
 * Client wrapper for the rate-lock-confirmation edge function.
 *
 * Two actions:
 *   - preview: returns the rendered email + recipients + gate state + lastSent info,
 *              plus the doc-type check and critical alert when the flag is on.
 *   - send:    actually sends the email; returns 409 on idempotent retry. While a
 *              critical alert is pending it queues the critical email instead.
 *
 * The edge function gates on:
 *   - JWT (401), org membership / loan visibility (404 via RLS), view_product_pricing (403).
 *   - Soft fails surfaced via gateState.disabledReasons on preview.
 *
 * Note: `supabase.functions.invoke` raises `FunctionsHttpError` for non-2xx
 * responses and returns `{ data: null, error, response }`. The structured JSON
 * body (e.g. `{ ok: false, code: 'already_sent', lastSentForCurrentSelection }`)
 * lives on the Response object only. We reuse the same parsing pattern as
 * useGroupedPricingSave so the modal can branch on `code` (already_sent /
 * send_in_flight / send_needs_review) instead of falling through to a generic
 * error toast.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('RateLockConfirmationService', 'rate-lock-confirmation');

const FUNCTION_NAME = 'rate-lock-confirmation';

async function readInvokeErrorBody(
  error: unknown,
  response: Response | undefined
): Promise<Record<string, unknown> | null> {
  const resp =
    response ??
    ((error as { context?: unknown } | null)?.context instanceof Response
      ? ((error as { context: Response }).context)
      : undefined);
  if (!(resp instanceof Response)) {
return null;
}
  try {
    return (await resp.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export type FailureReason =
  | 'notifications_disabled'
  | 'no_active_delivery_lock'
  | 'loan_funded'
  | 'brokered_loan'
  | 'no_lo_recipient';

export interface LastSentInfo {
  sentAt: string;
  sentByEmail: string | null;
  resendId: string | null;
}

export interface LastSentAnyInfo extends LastSentInfo {
  selectionId: string | null;
}

export interface SourceMeta {
  selectionId: string;
  lockVersionId?: string;
  /**
   * Legacy response names retained for compatibility with the Edge Function
   * payload. They describe the active lock version label/id shown in the UI;
   * the visible picker source is `lock_versions`, not `lock_snapshots`.
   */
  lockSnapshotId: string;
  snapshotLabel: string | null;
  capturedAt: string | null;
  selectedByEmail: string | null;
  selectedAt: string;
  updatedAt: string;
}

/** The preview's fresh documentation-type check against Optimal Blue. */
export interface DocTypeCheck {
  outcome: 'match' | 'mismatch' | 'not_checked' | 'superseded';
  /** Machine code; `reasonLabel` carries the plain words shown to the operator. */
  reason: string | null;
  reasonLabel: string | null;
  encompassValue: string | null;
  obValue: string | null;
  obReadAt: string | null;
  /** True when the background worker will check this lock again. */
  retryable: boolean;
}

export type CriticalAlertStatus = 'pending' | 'sending' | 'sent' | 'review_required';

/**
 * The mandatory "Rate Lock Confirmation – Critical" email for this lock
 * episode. It goes out instead of the normal confirmation when the doc types
 * disagree, sent by an operator or automatically at `sendAfter`.
 */
export interface CriticalAlert {
  checkId: string;
  status: CriticalAlertStatus;
  /** The values as found; the email shows them even if the doc type is fixed later. */
  encompassValue: string;
  obValue: string;
  foundAt: string;
  /** When the automatic send is due. */
  sendAfter: string | null;
  sentAt: string | null;
  sentVia: 'manual' | 'auto' | null;
  requestedByName: string | null;
}

export interface PreviewResponse {
  subject: string | null;
  html: string | null;
  recipients: { to: string[]; cc: string[] } | null;
  sourceMeta: SourceMeta | null;
  gateState: { canSend: boolean; disabledReasons: FailureReason[] };
  lastSentForCurrentSelection: LastSentInfo | null;
  lastSentAny: LastSentAnyInfo | null;
  /**
   * Server-generated UUID issued ONLY when `lastSentForCurrentSelection !== null`
   * (modal lands in resend mode). Carry back as `resendAttemptId` on the
   * `'resend'` action call. New preview → new UUID.
   */
  resendAttemptId: string | null;
  /**
   * Absent or null when `rate_lock_critical_alerts_v1` is off or the
   * preview's gates failed.
   */
  docTypeCheck?: DocTypeCheck | null;
  criticalAlert?: CriticalAlert | null;
}

export interface SendSuccess {
  ok: true;
  queued?: false;
  factId: string;
  communicationId: string | null;
  auditId: string | null;
  auditStatus: 'sent' | 'sent_pending_completion';
  sentTo: string[];
  sentCc: string[];
  resendId: string | null;
  /** Server-authored ISO timestamp; same value written to loan_notification_facts.sent_at. */
  sentAt: string;
  /** Authed user (clicked Send) — NOT the user who selected the delivery lock. */
  sentByEmail: string | null;
}

/**
 * A send while a critical alert is pending hands the critical email to the
 * background sender. It is being sent, not delivered: refresh the preview to
 * see `criticalAlert.status === 'sent'`.
 */
export interface SendQueued {
  ok: true;
  queued: true;
  criticalAlert: CriticalAlert;
}

export interface SendConflict {
  ok: false;
  code: 'already_sent' | 'send_in_flight' | 'send_needs_review' | 'never_sent';
  lastSentForCurrentSelection: LastSentInfo | null;
}

export interface SendFailure {
  ok: false;
  code: string;
  reasons?: FailureReason[];
  detail?: string;
}

export type SendResponse = SendSuccess | SendQueued | SendConflict | SendFailure;

export interface SendOptions {
  /**
   * This client shows a queued critical email as being sent. Without it the
   * server answers a queued critical send with `send_in_flight`.
   */
  acceptsQueued?: boolean;
  /** One id per modal open, reused on repeated clicks; a replay returns the original result. */
  requestId?: string;
}

export class RateLockConfirmationError extends Error {
  status?: number;
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'RateLockConfirmationError';
    this.status = status;
    this.code = code;
  }
}

export async function preview(encompassLoanId: string): Promise<PreviewResponse> {
  const { data, error, response } = await supabase.functions.invoke<PreviewResponse>(FUNCTION_NAME, {
    body: { encompassLoanId, action: 'preview' }
  });
  if (error) {
    const body = await readInvokeErrorBody(error, response);
    const code = typeof body?.code === 'string' ? (body.code as string) : undefined;
    const status = response?.status;
    log.error('preview failed', { encompassLoanId, code, status }, error);
    throw new RateLockConfirmationError(
      (typeof body?.detail === 'string' && body.detail) ||
        code ||
        error.message ||
        'Preview failed',
      status,
      code
    );
  }
  if (!data) {
    throw new RateLockConfirmationError('Preview returned no data');
  }
  return data;
}

async function dispatchSendOrResend(
  encompassLoanId: string,
  action: 'send' | 'resend',
  resendAttemptId?: string,
  options: SendOptions = {}
): Promise<SendResponse> {
  const requestBody: Record<string, unknown> = { encompassLoanId, action };
  if (action === 'resend') {
    requestBody.resendAttemptId = resendAttemptId;
  }
  if (options.acceptsQueued) {
    requestBody.acceptsQueued = true;
  }
  if (options.requestId) {
    requestBody.requestId = options.requestId;
  }
  const { data, error, response } = await supabase.functions.invoke<SendResponse>(FUNCTION_NAME, {
    body: requestBody
  });
  if (!error && data) {
return data;
}

  // Non-2xx path: parse the structured body. Edge function returns 409 with
  // `{ ok:false, code:'already_sent' | 'send_in_flight' |
  // 'send_needs_review' | 'never_sent', lastSentForCurrentSelection }`
  // on idempotent / preflight rejections, and 400 with `{ ok: false, code, reasons }`
  // on soft-gate failures. The modal branches on `code` to show the right toast.
  const body = await readInvokeErrorBody(error, response);
  const status = response?.status;

  if (body && body.ok === false && typeof body.code === 'string') {
    if (
      body.code === 'already_sent' ||
      body.code === 'send_in_flight' ||
      body.code === 'send_needs_review' ||
      body.code === 'never_sent'
    ) {
      return {
        ok: false,
        code: body.code,
        lastSentForCurrentSelection:
          (body.lastSentForCurrentSelection as LastSentInfo | null | undefined) ?? null
      };
    }
    return {
      ok: false,
      code: body.code,
      reasons: Array.isArray(body.reasons) ? (body.reasons as FailureReason[]) : undefined,
      detail: typeof body.detail === 'string' ? (body.detail as string) : undefined
    };
  }

  log.error(`${action} failed`, { encompassLoanId, status }, error instanceof Error ? error : undefined);
  throw new RateLockConfirmationError(
    error?.message || `${action[0].toUpperCase() + action.slice(1)} failed`,
    status
  );
}

export async function send(encompassLoanId: string, options: SendOptions = {}): Promise<SendResponse> {
  return dispatchSendOrResend(encompassLoanId, 'send', undefined, options);
}

/**
 * Resend the lock confirmation for the active selection. Requires a UUID
 * issued by the prior `preview` call (in `PreviewResponse.resendAttemptId`).
 * Same-attempt double-clicks within one modal session collide on the unique
 * key constraint and the server returns `send_in_flight` (in-progress),
 * `send_needs_review` (ambiguous prior dispatch), or `already_sent` (the same
 * attempt already settled).
 */
export async function resend(
  encompassLoanId: string,
  resendAttemptId: string,
  options: SendOptions = {}
): Promise<SendResponse> {
  return dispatchSendOrResend(encompassLoanId, 'resend', resendAttemptId, options);
}
